import { mealWeights, computeIntake } from './intake.js';
import { EVENT_LABELS, eventObservationDetail } from './events.js';
import { treatmentPlanAt } from './treatment-lifecycle.js';
function dateKey(epoch, timeZone) {
    return new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(new Date(epoch));
}
function datesBetween(from, to, timeZone) {
    const start = dateKey(from, timeZone);
    const end = dateKey(to, timeZone);
    const dates = [];
    let cursor = new Date(`${start}T00:00:00Z`);
    const boundary = new Date(`${end}T00:00:00Z`);
    while (cursor <= boundary && dates.length < 370) {
        dates.push(cursor.toISOString().slice(0, 10));
        cursor = new Date(cursor.getTime() + 86_400_000);
    }
    return dates;
}
function eventFact(event, comparison) {
    return {
        id: event.id,
        at: event.occurredAt,
        source: 'guardian',
        sourceLabel: '보호자 기록',
        title: EVENT_LABELS[event.type] ?? '직접 본 사실',
        detail: eventObservationDetail(event),
        originalRoute: 'event',
        attachmentIds: event.attachmentIds,
    };
}
/**
 * PET-CHANGE-FIRST-06 — 추론 없는 재진 준비 계산기.
 * 사용자가 명시한 comparison과 actor-attributed 돌봄 사실만 변화로 모은다.
 * 기록이 없는 날짜는 정상으로 보간하지 않고 별도 목록으로 반환한다.
 */
export function buildVisitBrief(input) {
    const timeZone = input.timeZone ?? 'Asia/Seoul';
    const inWindow = (at) => Number.isFinite(at) && at >= input.from && at <= input.to;
    const events = input.events.filter((item) => item.petId === input.petId && inWindow(item.occurredAt));
    const changedFacts = events
        .filter((item) => item.comparison === 'changed')
        .map((item) => eventFact(item, 'changed'));
    const usualFacts = events
        .filter((item) => item.comparison === 'usual')
        .map((item) => eventFact(item, 'usual'));
    const recordedAt = events.map((item) => item.occurredAt);
    for (const episode of input.careEpisodes.filter((item) => item.petId === input.petId)) {
        for (const checkin of episode.checkins.filter((item) => inWindow(item.at))) {
            recordedAt.push(checkin.at);
            if (checkin.mood !== 'changed')
                continue;
            changedFacts.push({
                id: `${episode.id}:${checkin.id}`,
                recordId: episode.id,
                at: checkin.at,
                source: 'caregiver',
                sourceLabel: `${checkin.actorLabel || episode.agreement.providerLabel || '돌봄자'} 기록`,
                title: '돌봄 중 평소와 달랐던 사실',
                detail: [checkin.note, checkin.meal, checkin.toilet, checkin.activity].filter(Boolean).join(' · '),
                originalRoute: 'care',
            });
        }
        for (const exception of episode.exceptions.filter((item) => inWindow(item.at))) {
            recordedAt.push(exception.at);
            changedFacts.push({
                id: `${episode.id}:${exception.id}`,
                recordId: episode.id,
                at: exception.at,
                source: 'caregiver',
                sourceLabel: `${exception.actorLabel || episode.agreement.providerLabel || '돌봄자'} 기록`,
                title: '돌봄 중 예외',
                detail: `${exception.observedFact} · 한 일: ${exception.actionTaken}`,
                originalRoute: 'care',
            });
        }
        const receipt = episode.returnReceipt;
        if (receipt && inWindow(receipt.returnedAt)) {
            recordedAt.push(receipt.returnedAt);
            changedFacts.push({
                id: `${episode.id}:return`,
                recordId: episode.id,
                at: receipt.returnedAt,
                source: 'caregiver',
                sourceLabel: `${receipt.actorLabel || episode.agreement.providerLabel || '돌봄자'} 반환`,
                title: '돌아온 뒤 확인',
                detail: [receipt.conditionFact, receipt.followup ? `다시 볼 것: ${receipt.followup}` : ''].filter(Boolean).join(' · '),
                originalRoute: 'care',
            });
        }
    }
    const treatmentNames = new Map(input.treatments.filter((item) => item.petId === input.petId).map((item) => [item.id, item]));
    const logs = input.treatmentLogs.filter((item) => item.petId === input.petId && inWindow(item.recordedAt));
    const treatmentResults = { given: 0, skipped: 0, refused: 0, partial: 0, total: logs.length };
    for (const log of logs) {
        treatmentResults[log.status] += 1;
        recordedAt.push(log.recordedAt);
        if (log.status === 'given')
            continue;
        const status = log.status === 'skipped' ? '건너뜀' : log.status === 'refused' ? '거부' : '일부만 함';
        changedFacts.push({
            id: log.id,
            at: log.recordedAt,
            source: 'treatment',
            sourceLabel: `${log.actorLabel || '보호자'} 기록`,
            title: treatmentNames.has(log.treatmentId) ? treatmentPlanAt(treatmentNames.get(log.treatmentId), log.recordedAt).name : '투약·처치',
            detail: `${status}${log.note ? ` · ${log.note}` : ''}`,
            originalRoute: 'treatment',
        });
    }
    const openQuestions = input.careQuestions
        .filter((item) => item.petId === input.petId && !item.answeredOn)
        .sort((left, right) => left.askedOn.localeCompare(right.askedOn) || left.id.localeCompare(right.id))
        .map((item) => ({ id: item.id, text: item.text, askedOn: item.askedOn, carriedFromEarlier: item.askedOn < dateKey(input.from, timeZone) }));
    changedFacts.sort((left, right) => right.at - left.at || left.id.localeCompare(right.id));
    usualFacts.sort((left, right) => right.at - left.at || left.id.localeCompare(right.id));
    const allFacts = [...changedFacts, ...usualFacts, ...events.filter((item) => !item.comparison || item.comparison === 'uncompared').map((item) => eventFact(item, 'uncompared'))];
    for (const log of logs.filter((item) => item.status === 'given'))
        allFacts.push({ id: log.id, at: log.recordedAt, source: 'treatment', sourceLabel: '보호자 실제 기록', title: treatmentNames.has(log.treatmentId) ? treatmentPlanAt(treatmentNames.get(log.treatmentId), log.recordedAt).name : '투약·처치', detail: `완료${log.note ? ` · ${log.note}` : ''}`, originalRoute: 'treatment' });
    const vitalLabels = { weight: '체중 kg', sleepingRespiratoryRate: '수면 호흡수 회/분', bloodGlucose: '혈당 mg/dL', temperature: '체온 °C', heartRate: '심박수 회/분' };
    for (const row of input.vitals ?? [])
        if (row.petId === input.petId && !row.deletedAt && !row.duplicateOf && inWindow(row.measuredAt))
            allFacts.push({ id: row.id, at: row.measuredAt, source: 'guardian', sourceLabel: '보호자 측정 기록', title: vitalLabels[row.type] ?? '측정값', detail: `${row.value}${row.note ? ` · ${row.note}` : ''}`, originalRoute: 'measurement' });
    for (const row of input.coughLogs ?? [])
        if (row.petId === input.petId && !row.deletedAt && !row.duplicateOf && row.date >= dateKey(input.from, timeZone) && row.date <= dateKey(input.to, timeZone)) {
            const sameDay = input.coughLogs.filter((item) => item.petId === row.petId && item.date === row.date && !item.deletedAt && !item.duplicateOf);
            const dayObserved = row.dayObserved ?? row.dayCount > 0;
            const nightObserved = row.nightObserved ?? row.nightCount > 0;
            if (!dayObserved && !nightObserved && sameDay.length < 2)
                continue;
            allFacts.push({ id: row.id, at: Date.parse(`${row.date}T00:00:00+09:00`), source: 'guardian', sourceLabel: '보호자 관찰일 기록', title: '기침 관찰', detail: sameDay.length > 1 ? '같은 날짜 원본이 여러 개 · 횟수 확인 필요' : `낮 ${dayObserved ? `${row.dayCount}회` : '미관찰'} · 밤 ${nightObserved ? `${row.nightCount}회` : '미관찰'}`, originalRoute: 'cough', attachmentIds: row.attachmentIds, observation: { label: '밤 기침', value: sameDay.length > 1 || !nightObserved ? null : row.nightCount, unit: '회' } });
        }
    for (const row of input.meals ?? [])
        if (row.petId === input.petId && !row.deletedAt && !row.duplicateOf && inWindow(row.servedAt)) {
            const weight = mealWeights(row), intake = computeIntake(row);
            const ratio = weight.servedG > 0 && weight.leftoverG != null && intake.ok ? Math.round(weight.leftoverG / weight.servedG * 1000) / 10 : null;
            allFacts.push({ id: row.id, at: row.servedAt, source: 'guardian', sourceLabel: '보호자 식사 기록', title: '먹기', detail: `${row.foodName} · 준 양 ${weight.servedG}g · ${weight.leftoverG == null ? '남긴 양 미확인' : `남긴 양 ${weight.leftoverG}g`}`, originalRoute: 'meal', observation: { label: '먹기', value: ratio, unit: '% 남김' } });
        }
    for (const row of input.gaitLogs ?? [])
        if (row.petId === input.petId && !row.deletedAt && !row.duplicateOf && inWindow(row.startedAt)) {
            const minutes = Math.round(row.durationSec / 60 * 10) / 10;
            allFacts.push({ id: row.id, at: row.startedAt, source: 'guardian', sourceLabel: '보호자 산책 기록', title: '산책', detail: `${minutes}분${row.distanceM == null ? '' : ` · ${row.distanceM}m`}${row.stoppedEarly ? ' · 일찍 마침' : ''}`, originalRoute: 'gait', attachmentIds: row.attachmentIds, observation: { label: '산책', value: minutes, unit: '분' } });
        }
    allFacts.sort((a, b) => b.at - a.at || a.id.localeCompare(b.id));
    const recordedDates = [...new Set([...recordedAt, ...allFacts.map((fact) => fact.at)].map((at) => dateKey(at, timeZone)))].sort();
    const recorded = new Set(recordedDates);
    const allDays = datesBetween(input.from, input.to, timeZone);
    const quiet = new Set((input.dayMarks ?? []).filter((mark) => mark.petId === input.petId && !mark.deletedAt && mark.kind === 'quiet').map((mark) => mark.date));
    const quietDates = allDays.filter((day) => quiet.has(day));
    const noRecordDates = allDays.filter((day) => !recorded.has(day) && !quiet.has(day));
    return {
        from: input.from,
        to: input.to,
        changedFacts,
        allFacts,
        usualFacts,
        openQuestions,
        treatmentResults,
        recordedDates,
        noRecordDates,
        quietDates,
        totalDays: allDays.length,
        comparisonDisclosure: '평소와 다름 표시는 보호자와 돌봄자가 직접 남긴 선택입니다. 기록이 없는 날은 상태를 확인할 수 없습니다.',
        hasAnythingToBring: changedFacts.length > 0 || openQuestions.length > 0 || logs.length > 0,
    };
}
export function buildVisitBriefText(petName, brief) {
    const lines = [
        `${petName} · 이번 진료에 가져갈 기록`,
        `${dateKey(brief.from, 'Asia/Seoul')} ~ ${dateKey(brief.to, 'Asia/Seoul')}`,
        '',
        `평소와 달랐다고 남긴 사실 ${brief.changedFacts.length}건`,
        ...brief.changedFacts.map((fact) => `- ${dateKey(fact.at, 'Asia/Seoul')} · ${fact.title} · ${fact.detail} · ${fact.sourceLabel}`),
        '',
        `아직 물어볼 질문 ${brief.openQuestions.length}건`,
        ...brief.openQuestions.map((question) => `- ${question.text} · ${question.askedOn}부터`),
        '',
        `투약·처치 결과 ${brief.treatmentResults.total}건 · 완료 ${brief.treatmentResults.given} · 건너뜀 ${brief.treatmentResults.skipped} · 거부 ${brief.treatmentResults.refused} · 일부 ${brief.treatmentResults.partial}`,
        recordDaySummary(brief),
        '',
        brief.comparisonDisclosure,
        '진단이나 치료 지시가 아니라 보호자와 돌봄자가 남긴 사실입니다.',
        ...(brief.noRecordDates.length ? ['', `부록 · 기록도 확인 표시도 없는 날 ${brief.noRecordDates.length}일`, compactDateRanges(brief.noRecordDates)] : []),
    ];
    return lines.join('\n');
}
/** "기록한 날 12/90일 · 별일 없음 확인한 날 3일" — 날짜를 늘어놓지 않는 한 줄 요약. */
export function recordDaySummary(brief) {
    return recordDayCountSummary(brief.recordedDates.length, brief.totalDays, brief.quietDates.length);
}
/** Shared wording for the original brief and period-report PDF; counts remain supplied facts. */
export function recordDayCountSummary(recordedDays, totalDays, quietDays = 0) {
    return `기록한 날 ${recordedDays}/${totalDays}일${quietDays ? ` · 별일 없음 확인한 날 ${quietDays}일` : ''}`;
}
/** 이어진 날짜는 "08-01~08-09"처럼 묶어 부록 길이를 줄인다. */
export function compactDateRanges(dates) {
    const sorted = [...dates].sort();
    const ranges = [];
    let start = sorted[0];
    let prev = sorted[0];
    const next = (date) => { const value = new Date(`${date}T00:00:00Z`); value.setUTCDate(value.getUTCDate() + 1); return value.toISOString().slice(0, 10); };
    for (const date of sorted.slice(1)) {
        if (prev && next(prev) === date) {
            prev = date;
            continue;
        }
        if (start && prev)
            ranges.push(start === prev ? start.slice(5) : `${start.slice(5)}~${prev.slice(5)}`);
        start = date;
        prev = date;
    }
    if (start && prev)
        ranges.push(start === prev ? start.slice(5) : `${start.slice(5)}~${prev.slice(5)}`);
    return ranges.join(', ');
}
